"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.iterateUniversalExportIssues = iterateUniversalExportIssues;
exports.buildHeader = buildHeader;
exports.buildUniversalExport = buildUniversalExport;
const accessibility_standards_1 = __importDefault(require("@deque/accessibility-standards"));
const uuid_1 = require("uuid");
const mobilePlatform_1 = require("../mobilePlatform");
/**
 * Fixed namespace UUID for `issueId` derivation. NEVER change this value —
 * doing so invalidates issueId stability across every previously-emitted
 * artifact.
 */
const ISSUE_ID_NAMESPACE = '8b6a3f1c-2f7d-5e9a-9b4c-3d4f5a6b7c8d';
const UNKNOWN = 'unknown';
/**
 * Mobile results carry platform/app metadata stamped by the mobile→web
 * converter (`convertMobileResult`) and preserved through dedupe; web results
 * never populate these fields.
 */
function isMobilePageState(pageState) {
    const meta = pageState?.metadata;
    return ((0, mobilePlatform_1.normaliseMobileOS)(meta?.platform?.name) !== null ||
        typeof meta?.appIdentifier === 'string');
}
/**
 * Mobile has no HTML source; the converter packs the scan's analyzed values
 * (className, detected text, color contrasts, …) into the node's `html` slot
 * as a JSON string. Parse it back to an object for `analyzedValues`, or
 * return undefined when missing/malformed so the field is omitted.
 */
function parseAnalyzedValues(html) {
    if (!html)
        return undefined;
    try {
        const parsed = JSON.parse(html);
        return typeof parsed === 'object' && parsed !== null ? parsed : undefined;
    }
    catch {
        return undefined;
    }
}
/**
 * Internal: map a single deduped node → `UniversalIssue`.
 *
 * Object-literal key order here defines the on-wire key order under
 * `JSON.stringify` (V8 preserves insertion order). Do not reorder casually —
 * it changes the byte-exact output and breaks the deterministic-byte
 * assertions in the streaming write tests.
 *
 * Walnut's `UniversalIssue` is looser than the CEF spec: `issueId` allows
 * null, `axeVersion` is optional, `impact` allows null, and there are
 * extension/mobile/ignore fields we never populate. We populate the
 * CEF-required fields with concrete values regardless — the wire output
 * stays spec-compliant; the type just won't catch a missed field at compile
 * time.
 */
function mapIssue(session, result, unique, pageState, needsReview) {
    // `fingerprints` are rule + url + selector with no status in the mix, so the
    // same node can be a violation on one page state and Needs Review on another
    // and reach both tracks — which dedupe against separate key sets — with an
    // identical fingerprint. Discriminating the Needs Review name keeps the two
    // entries from colliding on `issueId`. Violations keep their existing
    // derivation byte-for-byte.
    const issueIdName = unique.fingerprints
        ? needsReview
            ? `${unique.fingerprints.toString()}|INCOMPLETE`
            : unique.fingerprints.toString()
        : (0, uuid_1.v4)();
    const issue = {
        issueId: (0, uuid_1.v5)(issueIdName, ISSUE_ID_NAMESPACE),
        ruleId: result.id,
        description: result.description,
        help: result.help,
        helpUrl: result.helpUrl,
        impact: result.impact,
        needsReview,
        // Parity with devtools-mobile-service's `AxeUniversalFormatRuleResult.status`.
        // Needs Review only: stamping 'FAIL' on violations would change both the
        // flag-off bytes and the flag-on-but-no-needs-review bytes.
        ...(needsReview && { status: 'INCOMPLETE' }),
        isManual: false,
        isExperimental: result.tags.includes('experimental'),
        isAdvanced: result.tags.includes('advanced'),
        summary: unique.failureSummary,
        tags: result.tags,
        createdAt: unique.created_at.toISOString(),
        fingerprints: unique.fingerprints,
        shareURL: null,
        integrationUrl: null,
        axeVersion: session.axe_core_version ?? session.data_source_version ?? UNKNOWN
    };
    if (unique !== undefined) {
        // The typing from Walnut's `UniversalIssue` is wrong
        issue.selector = unique.target;
    }
    if (isMobilePageState(pageState)) {
        // Mobile issues have no HTML source — the spec wants `source: null` with
        // the scan's analyzed values (packed into `html` as a JSON string by
        // `convertMobileResult`) emitted under `analyzedValues` instead.
        // Walnut's `source?: string` typing doesn't admit the spec's null.
        issue.source = null;
        const analyzedValues = parseAnalyzedValues(unique.html);
        if (analyzedValues !== undefined) {
            issue.analyzedValues = analyzedValues;
        }
    }
    else if (unique.html) {
        issue.source = unique.html;
    }
    if (pageState.url) {
        issue.testUrl = pageState.url;
    }
    if (pageState.page_state) {
        issue.testPageTitle = pageState.page_state;
    }
    return issue;
}
/**
 * Public: per-issue generator. The streaming serializer iterates this so
 * memory carries at most one mapped issue at a time.
 *
 * Input order is trusted — the deduped artifact in S3 is already canonically
 * ordered by the dedupe step, so we just walk it.
 */
function* iterateUniversalExportIssues(session, pageStateResults, needsReviewPageStateResults = []) {
    for (const pageState of pageStateResults) {
        for (const result of pageState.result) {
            for (const unique of result.uniques) {
                yield mapIssue(session, result, unique, pageState, false);
            }
        }
    }
    // Appended, never interleaved: violation entries and their order must stay
    // byte-identical to a violations-only export.
    for (const pageState of needsReviewPageStateResults) {
        for (const result of pageState.result) {
            for (const unique of result.uniques) {
                yield mapIssue(session, result, unique, pageState, true);
            }
        }
    }
}
function aggregate(pageStateResults, needsReviewPageStateResults) {
    const issueSummary = {
        critical: 0,
        serious: 0,
        moderate: 0,
        minor: 0,
        bestPractices: 0,
        needsReview: 0
    };
    const ruleCounts = new Map();
    let firstPageState = null;
    let earliest = null;
    let latest = null;
    for (const pageState of pageStateResults) {
        const ts = pageState.created_at.getTime();
        if (earliest === null || ts < earliest) {
            earliest = ts;
            firstPageState = pageState;
        }
        if (latest === null || ts > latest) {
            latest = ts;
        }
        for (const result of pageState.result) {
            const impact = result.impact;
            const nodeCount = result.uniques.length;
            issueSummary[impact] += nodeCount;
            ruleCounts.set(result.id, (ruleCounts.get(result.id) ?? 0) + nodeCount);
        }
    }
    const failedRules = [...ruleCounts.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, count]) => ({ name, count, mode: 'automated' }));
    // Needs Review deliberately contributes to NO impact bucket and no
    // `failedRules` entry, so those counts keep summing to the total issue count.
    // This diverges from the extension's `getIssueSummary`, which folds
    // needs-review into the impact buckets. Per-rule counts are instance counts
    // over the already-deduplicated input — the extension's
    // `getIssueRules(issues, true)` method — so the summary reconciles with the
    // entries emitted into `allIssues`.
    const needsReviewCounts = new Map();
    for (const pageState of needsReviewPageStateResults) {
        for (const result of pageState.result) {
            const nodeCount = result.uniques.length;
            issueSummary.needsReview += nodeCount;
            needsReviewCounts.set(result.id, (needsReviewCounts.get(result.id) ?? 0) + nodeCount);
        }
    }
    const needsReviewRules = [...needsReviewCounts.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, count]) => ({ name, count, mode: 'automated' }));
    return {
        issueSummary,
        failedRules,
        needsReviewRules,
        firstPageState,
        testingStartDate: earliest === null ? null : new Date(earliest).toISOString(),
        testingEndDate: latest === null ? null : new Date(latest).toISOString()
    };
}
/**
 * Public: build the universal-export document header WITHOUT `allIssues`. The
 * streaming serializer writes this as the document prefix, then streams
 * individual issues from `iterateUniversalExportIssues` into the open
 * `allIssues` array.
 *
 * Key-order discipline: the returned object's literal field order IS the
 * on-wire order under `JSON.stringify`. Keep insertion order stable.
 */
function buildHeader(session, pageStateResults, needsReviewPageStateResults = []) {
    const agg = aggregate(pageStateResults, needsReviewPageStateResults);
    // `new Date(...)` coercion: the session may have crossed a JSON boundary
    // (Lambda event payload), flattening `Date` fields to ISO strings — calling
    // `.toISOString()` on those directly throws. `new Date()` accepts both
    // shapes (cloning a `Date`, parsing a string).
    const startDate = agg.testingStartDate ?? new Date(session.created_at).toISOString();
    const endDate = agg.testingEndDate ??
        new Date(session.updated_at ?? session.created_at).toISOString();
    // The chronologically-first page state is representative for the session:
    // a session is all-mobile or all-web (results arrive through a single
    // ingestion route per session), so one page state classifies the document.
    //
    // Platform detection mirrors ingestion's `detectMobilePlatform` fallback
    // chain (`platform.name` = raw OS string, then `device.name` — e.g.
    // "iPhone 15" → iOS).
    const firstMetadata = agg.firstPageState?.metadata;
    const mobilePlatform = (0, mobilePlatform_1.normaliseMobileOS)(firstMetadata?.platform?.name) ??
        (0, mobilePlatform_1.normaliseMobileOS)(firstMetadata?.device?.name);
    const isMobile = isMobilePageState(agg.firstPageState);
    const axeVersion = session.axe_core_version ?? session.data_source_version ?? UNKNOWN;
    const sessionSettings = session.settings;
    const rawStandard = sessionSettings?.accessibilityStandard?.value;
    // Mobile is not yet in global configuration, so its standard is fixed.
    // Web: map the raw enum to its display name ('wcag22aa' → 'WCAG 2.2 AA'),
    // falling back to the raw value for unmapped standards.
    const standard = isMobile
        ? 'WCAG 2.2 AA'
        : rawStandard !== undefined
            ? (accessibility_standards_1.default[rawStandard]?.name ?? rawStandard)
            : UNKNOWN;
    const bestPracticesEnabled = sessionSettings && sessionSettings.bestPractices?.value === 'enabled';
    const experimentalEnabled = sessionSettings && sessionSettings.experimentalRules?.value === 'enabled';
    // A mobile page state with no resolvable platform is Android by ingestion's
    // invariant: `detectMobilePlatform` rejects results it can't place on a
    // platform, with one exception — legacy Android scans missing axeDevice
    // os/name, admitted via a className heuristic.
    const engine = !isMobile
        ? 'axe-core'
        : mobilePlatform === null || mobilePlatform === mobilePlatform_1.MobilePlatform.ANDROID
            ? 'axe-android'
            : 'axe-ios'; // iOS and iPadOS both ship axe-ios
    const source = {
        productName: isMobile ? 'axe-devtools-mobile' : 'axe-devtools-html',
        productComponentName: session.data_source ?? UNKNOWN,
        productVersion: session.data_source_version ?? UNKNOWN
    };
    const testDetails = {
        testId: session.id,
        testName: session.name ?? '',
        // The email of the user who created the session, captured at session-init
        // (results-team#369). Null for sessions created before that shipped, or
        // when the creator has no resolvable email (e.g. pipeline / API-key-only).
        createdBy: session.created_by_user_email ?? null,
        startDate,
        endDate,
        engine,
        axeVersion,
        standard,
        bestPracticesEnabled: bestPracticesEnabled === true,
        experimentalEnabled: experimentalEnabled === true,
        integrationTestId: null
    };
    // Top level object "header". `standard` always resolves (falling back to
    // 'unknown'), so it's emitted unconditionally; the settings-derived flag
    // fields are inserted conditionally so they're omitted (not emitted as
    // `undefined`) when session.settings is null.
    const header = {
        axeVersion,
        standard,
        testingStartDate: startDate,
        testingEndDate: endDate,
        ...(bestPracticesEnabled !== null && { bestPracticesEnabled }),
        ...(experimentalEnabled !== null && { experimentalEnabled }),
        issueSummary: agg.issueSummary,
        failedRules: agg.failedRules,
        needsReview: agg.needsReviewRules,
        source,
        testDetails
    };
    return header;
}
/**
 * Public: one-shot universal-export builder. Used by tests and any
 * non-streaming consumer; the streaming write path uses `buildHeader`
 * + `iterateUniversalExportIssues` instead so `allIssues` never materializes
 * in memory in full.
 */
function buildUniversalExport(session, pageStateResults, needsReviewPageStateResults = []) {
    return {
        ...buildHeader(session, pageStateResults, needsReviewPageStateResults),
        allIssues: [
            ...iterateUniversalExportIssues(session, pageStateResults, needsReviewPageStateResults)
        ]
    };
}
//# sourceMappingURL=index.js.map